<template>
  <div>
    <ProgressBar />
    <div class="mx-auto max-w-7xl px-4 pb-12 pt-8 sm:px-6 lg:px-8 sm:py-12">
      <ProcessingModal />
      <div class="mx-auto max-w-3xl">
        <h3 class="text-xl font-semibold leading-8 text-green-400 sm:col-span-6 md:text-3xl lg:text-3xl">
          Gleich geschafft!
        </h3>

        <form @submit="onSubmit">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
              <Subheadline
                subheadline="Um Ihren individuellen Preis zu ermitteln, benötigen wir noch folgende Informationen:"
                helptext-headline="Höhe der Mietkaution & Beitragshöhe"
                :helptext="'Bitte geben Sie die Höhe Ihrer Mietkaution gemäß Mietvertrag an. Laut Gesetz darf die Mietkaution nicht höher als das 3-fache der Kaltmiete betragen. Die Höhe des Beitrages liegt bei ' + tenant.priceRateString + ' der Kautionssumme.'" />
              <Deposit />
              <Subheadline subheadline="Wann ziehen Sie in Ihre neue Wohnung und ab wann benötigen Sie die Bürgschaft?"
                helptext-headline="Vertragsbeginn"
                helptext="Die Mietbürgschaft wird ab dem Vertragsbeginn aktiv. Schäden, die vor diesem Datum entstanden sind, sind nicht gedeckt (z. B. Mietschulden vor Beginn der Bürgschaft). Sollten Sie bereits vor dem Mietbeginn Zugang zur Wohnung haben, empfehlen wir Ihnen, den Vertragsbeginn auf einen Zeitpunkt vor dem Mietbeginn zu legen. Liegt der Mietbeginn in der Vergangenheit, ist der Vertragsbeginn automatisch der Tag nach dem Abschluss. Der frühste Vertragsbeginn ist der Tag nach dem Abschluss." />
              <DatePicker id="next_moveInDate" label="Mietbeginn" />
              <DatePicker id="next_contractStart" label="Vertragsbeginn der Bürgschaft" :disabled="contractDisabled" />
              <Subheadline subheadline="Wie lautet Ihre neue Adresse?"
                helptext-headline="Wozu benötigen wir die Adressdaten Ihrer neuen Wohnung?"
                helptext="Die Adressdaten Ihrer neuen Wohnung werden benötigt, um die Mietkautionsbürgschaft für dieses Objekt als Mietsicherheit zu hinterlegen. Nur wenn wir diese Daten erhalten, kann die Bürgschaft abgeschlossen und für Sie als sichere Alternative zur Barkaution verwendet werden." />
              <Address addressType="property" />
              <Subheadline subheadline="Angaben zu Ihrem Vermieter" helptext-headline="Angaben zum Vermieter"
                helptext="Die vollständigen Daten Ihres Vermieters können Sie aus dem Mietvertrag übernehmen. Vertretungen (z. B. Hausverwaltungen) müssen nicht übernommen werden." />
              <InputLabeled id="next_landlordName" label="Vermieter Name" placeholder="Erika Musterfrau" />
              <InputLabeled id="next_landlordEmail" label="Vermieter E-Mail-Adresse"
                placeholder="erika.musterfrau@gmail.com" />
              <div class="my-3 text-sm font-medium sm:col-span-6 text-gray-700">
                Hinweis: Die Mietkautionsbürgschaft stellt die Sicherheit für Ihren Vermieter dar. Aus diesem Grund kann
                der Vertrag nach Abschluss nur durch den Vermieter beendet werden.
              </div>
              <Subheadline subheadline="Versand der Bürgschaft"
                helptext-headline="Digitale oder gedruckte Bürgschaftsurkunde verschicken"
                helptext="Die digitale Bürgschaft wird wenige Minuten nach Abschluss an Sie und Ihren Vermieter verschickt. Der Versand der gedruckten Bürgschaft erfolgt innerhalb von 2 Werktagen, ein Versand ins Ausland ist nicht möglich. Sollte die Adresse Ihres Vermieters im Ausland liegen, dann kontaktieren Sie uns bitte unter der kostenlosen Hotline 0211 54 26 83 00." />
              <Destination :property-address="tenant.formattedPropertyAddress" />
            </div>
            <div class="pt-8">
              <div class="justify-items-center">
                <div class="text-center sm:col-span-6">
                  <h3 class="text-xl font-semibold leading-8 text-green-400 md:text-3xl lg:text-3xl">
                    Nur noch ein Schritt!
                  </h3>
                </div>
                <button type="button" class="flex items-center justify-center mt-10 mx-auto px-4 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white
                                bg-green-400 hover:bg-green-300 sm:px-8 w-full sm:w-auto" @click="openIbanModal">
                  Zur Zahlung
                </button>
              </div>
            </div>
          </div>
          <input type="hidden" id="next_email" name="next[email]" :value="tenant.userData?.email" aria-invalid="false">
          <Iban @update:open="handleIbanClose" />
        </form>
      </div>
    </div>
    <AdvantagesSummary />
  </div>
</template>

<script setup>
import Subheadline from "../components/Subheadline.vue";
import InputLabeled from "../components/InputLabeled.vue";
import Destination from "../components/Destination.vue";
import { nextTick, onMounted, ref } from "vue";
import DatePicker from "../components/DatePicker.vue";
import { useTenantStore } from "../stores/tenant";
import Iban from "../components/Iban.vue";
import emitter from "../../common/utils/eventbus";
import AdvantagesSummary from "../components/AdvantagesSummary.vue";
import Deposit from "../components/Deposit.vue";
import ProgressBar from "../components/ProgressBar.vue"; //php Storm is wrong her ProgressBar is used
import { useProgressBarStore } from "../stores/progress-bar";
import ProcessingModal from "../components/Processing.vue";
import Address from "../components/Address.vue";
import { watch } from "vue";

const props = defineProps({
  id: String,
})

const progressBar = useProgressBarStore();
const tenant = useTenantStore();
const isDisabled = ref(false);
const contractDisabled = ref(false);


onMounted(async () => {
  await tenant.loadUserData(props.id);

  if (tenant.state !== 'reservation_issued' && tenant.state !== 'alteos_accepted') {
    tenant.navigate();
  }

  // prefill and set form values for vee
  tenant.prefillUserData();
  await nextTick();

  tenant.scrollToError();

  progressBar.setStepsState('next');
  tenant.setProcessingRequestedState('finished')
})

function handleIbanClose(newOpenValue) {
  tenant.processing.isIbanModalOpen = newOpenValue;
}
async function openIbanModal() {
  const isValid = await tenant.validateSection('next');
  if (!isValid) {
    tenant.scrollToError();
    return;
  }
  tenant.processing.isIbanModalOpen = true;
}

function modalNeedsToClose() {
  const errorArray = Object.keys(tenant.errors);
  if ((errorArray.length === 1) && (errorArray.includes('next_IBAN') || errorArray.includes('next_sepaTerms'))) {
    return false
  }
  return !((errorArray.length === 2) && (errorArray.includes('next_IBAN') && errorArray.includes('next_sepaTerms')));

}
function handleUnexpectedError(error, hashId) {
  window.location.href = `/error?id=${hashId}`;
}
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

emitter.on('submit-form', () => {
  onSubmit()
});

async function onSubmit() {
  tenant.setProcessingStartState(tenant.state);// save current state to not redirect without a change in state
  isDisabled.value = true;

  tenant.setProcessingModalOpen(true);
  const isValid = await tenant.validateSection('next'); // Validiere das gesamte Formular erneut

  if (!isValid) {
    isDisabled.value = false;
    tenant.setProcessingModalOpen(false);
    tenant.scrollToError();
    return;
  }
  try {
    tenant.setProcessingShowSpinnerOne(true);
    await tenant.postNextData(tenant.preparedNextData);
  } catch (error) {
    const statusCode = error?.response?.status || null;
    const validationErrors = error?.response?.data?.violations;

    switch (statusCode) {
      case 401: // Unauthorized
      case 403: // Forbidden
      case 404: // Not Found
      case 500: // Internal Server Error
        handleUnexpectedError(error, tenant.hashId);
        return;
      case 400: // Bad Request
      case 422: // Unprocessable Entity
        if (validationErrors?.length > 0) {
          const errors = tenant.handleBackEndValidations(validationErrors, 'next_')
          tenant.errors = { ...tenant.errors, ...errors };
          tenant.processing.isIbanModalOpen = !modalNeedsToClose(); //must IBAN modal be closed to show error
          if (errors.next_requestedState) {
            window.location.replace(`/error?id=${tenant.hashId}`);
          }
          isDisabled.value = false;
          tenant.resetProcessing();
          tenant.scrollToError();

          return;
        } else {
          handleUnexpectedError(error, tenant.hashId);
          return;
        }
      default:
        handleUnexpectedError(error, tenant.hashId);
        return;
    }
  }

  await sleep(3000)
  // Step 2. Send GET to get state 
  tenant.setProcessingShowSpinnerTwo(true);
  await sleep(3000)
  await tenant.loadUserData();

  // Step 3. redirect to next page
  tenant.setProcessingShowSpinnerThree(true);
  await sleep(1000)
  await tenant.handlePendingState(); // Redirect to next page

};

// watch on change of moveInDate or contractStart and validate both fields (deposit and rent)
watch([() => tenant.formValues.next_moveInDate, () => tenant.formValues.next_contractStart], () => {
  if (new Date(tenant.formValues.next_moveInDate) < (new Date()).setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000) {
    contractDisabled.value = true;
    tenant.formValues.next_contractStart = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  } else {
    contractDisabled.value = false;
  }
  if (tenant.formValues.next_contractStart !== '') {
    tenant.validateField('next_moveInDate', tenant.formValues.next_moveInDate);
    tenant.validateField('next_contractStart', tenant.formValues.next_contractStart);
  }
}, { immediate: true });

</script>
